// src/utils/frameFlowMap.ts


export type FlowId =
  | 'legal_essentials'
  | 'about'
  | 'legacy'
  | 'inventory'
  | 'funeral'
  | 'personal'
  | 'debts'
  | 'pets'
  | 'distribution';

type FlowConfig = {
  frames: ReadonlySet<number>;
  entry: number;     // where “start flow” should go
  summary: number;   // where “view summary” should go
  steps?: ReadonlyArray<FlowStep>;
};

type FlowStep = {
  id: string;
  label: string;
  frames: ReadonlyArray<number>; // ordered within the step if you care
};

export const FLOW_CONFIG: Record<FlowId, FlowConfig> = {
  legal_essentials: {
    frames: new Set([214, 215, 260, 261]),
    entry: 260,
    summary: 215,
    steps: [
      { id: 'legal_essentials_intro', label: 'Complete Your Legal Essentials', frames: [260] },
      { id: 'legal_essentials_documents', label: 'Upload Important Legal Documents', frames: [214] },
      { id: 'legal_essentials_executors', label: 'Choose Your Executor', frames: [261] },
    ],
  },
  about: {
    frames: new Set([132, 133, 135, 137, 138]),
    entry: 132,
    summary: 135,
    steps: [
      { id: 'about_intro', label: 'Set Up Your Plan', frames: [132] },
      { id: 'about_basic_info', label: 'Let\'s start with some basic information about you.', frames: [133] },
      { id: 'about_contacts', label: 'Planning your estate is a family affair.', frames: [137] },
      { id: 'about_password', label: 'Access is everything.', frames: [138] },
    ]
  },
  legacy: {
    frames: new Set([157, 172, 173]),
    entry: 157,
    summary: 173,
    steps: [
      {id: 'legacy_intro', label: 'Thinking About Your Legacy', frames:[157]},
      {id: 'legacy_person', label: 'Memories for your loved ones', frames: [172] },
    ]
  },
  inventory: {
    frames: new Set([186, 187, 188, 189, 191, 190, 213, 174, 239, 240],),
    entry: 174,
    summary: 213,
    steps: [
      {id: 'inventory_intro', label: 'Inventory Your Financial Assets and Accounts', frames: [174]},
      {id: 'inventory_accounts', label: 'Cash and Deposit Accounts', frames: [186] },
      {id: 'inventory_investment_accounts', label: 'Investment Accounts', frames: [187] },
      {id: 'inventory_retirement_accounts', label: 'Retirement Accounts', frames: [188] },
      {id: 'inventory_fin_assets', label: 'Other Financial Assets', frames: [189] },
      {id: 'inventory_income_sources', label: 'Income Sources', frames: [191]},
      {id: 'inventory_properties', label: 'Properties', frames: [190] },
      {id: 'inventory_tax_doc_records', label: 'Tax Documents and Records', frames: [239] },
      {id: 'inventory_tax_doc_summ', label: 'Tax Documents Summary', frames: [240] },
    ]
  },
  funeral: {
    frames: new Set([219, 221, 222, 223, 224]),
    entry: 219,
    summary: 223,
    steps: [
      { id: 'funeral_intro', label: 'Plan Your Funeral and Memorial', frames: [219] },
      { id: 'funeral_mem_services', label: 'Funeral and Memorial Service Preferences', frames: [221] },
      { id: 'funeral_mem_celebration', label: 'Memorial & Celebration Details', frames: [222] },
    ]
  },

  personal: {
    frames: new Set([800, 801, 802]),
    entry: 800,
    summary: 802,
    steps: [
      {id: 'personal_intro', label: 'Organize Your Personal Accounts and Belongings', frames: [800]},
      {id: 'personal_belongings', label: 'Personal Belongings', frames: [801] },
    ]
  },
  debts: {
    frames: new Set([234, 235, 236, 237, 238]),
    entry: 234,
    summary: 238,
    steps: [
      { id: 'debts_intro', label: 'Track Debts and Final Expenses', frames: [234] },
      { id: 'debts_track_debts', label: 'Track Your Debts and Liabilities', frames: [235] },
      { id: 'debts_liabilities_summ', label: 'Debts and Liabilities Summary', frames: [236] },
      { id: 'debts_track_mon_expenses', label: 'Track Your Monthly Expenses', frames: [237] },
    ]
  },
  pets: {
    frames: new Set([241, 242, 243, 244, 248, 249]),
    entry: 241,
    summary: 248,
    steps: [
      {id: 'pets_intro', label: 'Plan for Your Pets and Dependents', frames: [241] },
      {id: 'pets_add_pets', label: 'Add Your Pets', frames: [242] },
      {id: 'pets_add_dependents', label: 'Add Your Dependents', frames: [249] },
      {id: 'pets_financial_arr', label: 'Financial Arrangements for Care', frames: [243] },
      {id: 'pets_final_instructions', label: 'Final Instructions and Completion', frames: [244] },
    ]
  },
  distribution: {
    frames: new Set([245, 246, 247]),
    entry: 245,
    summary: 247,
    steps: [
      {id: 'distribution_intro', label: 'Assign and Approve Asset Distribution', frames: [245] },
      {id: 'distribution_ass_beneficiaries', label: 'Assign Beneficiaries', frames: [246] },
    ]
  }
};

/////////////////////////////////////////////////////////////////

export function flowIdForFrame(frame: number): FlowId | null {
  for (const flowId of Object.keys(FLOW_CONFIG) as FlowId[]) {
    if (FLOW_CONFIG[flowId].frames.has(frame)) return flowId;
  }
  return null;
}

/** Validates whether a frame belongs to a given flow. */
export function frameBelongsToFlow(frame: number, flowId: FlowId): boolean {
  return FLOW_CONFIG[flowId]?.frames.has(frame) ?? false;
}

/** True if this frame is the summary frame for the flow. */
export function isSummaryFrame(flowId: FlowId, frame: number): boolean {
  return FLOW_CONFIG[flowId].summary === frame;
}

/** Returns the configured summary frame for a flow. */
export function getSummaryFrame(flowId: FlowId): number {
  return FLOW_CONFIG[flowId].summary;
}

/** Returns the configured entry (first) frame for a flow. */
export function getEntryFrame(flowId: FlowId): number {
  return FLOW_CONFIG[flowId].entry;
}

/**
 * Given a flowId and optional lastFrame from DB, return the correct frame to navigate to.
 * - If lastFrame is valid and belongs to the flow -> use it
 * - Else fall back to the flow entry
 */
export function resolveResumeFrame(flowId: FlowId, lastFrame: number | null | undefined): number {
  const cfg = FLOW_CONFIG[flowId];

  if (typeof lastFrame === 'number' && Number.isInteger(lastFrame) && lastFrame >= 0) {
    if (cfg.frames.has(lastFrame)) return lastFrame;
  }

  return cfg.entry;
}

/**
 * Dev-only validation to catch mapping mistakes early.
 * - entry must be within frames
 * - summary must be within frames
 */
function assertFlowMapIsConsistent() {
  for (const flowId of Object.keys(FLOW_CONFIG) as FlowId[]) {
    const { frames, entry, summary } = FLOW_CONFIG[flowId];

    if (!frames.has(entry)) {
      // eslint-disable-next-line no-console
      console.warn(
        `[frameFlowMap] FLOW_CONFIG[${flowId}].entry = ${entry} is NOT in FLOW_CONFIG[${flowId}].frames`
      );
    }

    if (!frames.has(summary)) {
      // eslint-disable-next-line no-console
      console.warn(
        `[frameFlowMap] FLOW_CONFIG[${flowId}].summary = ${summary} is NOT in FLOW_CONFIG[${flowId}].frames`
      );
    }
  }
}

// Run once on module import (dev only)
if (import.meta.env?.DEV) {
  assertFlowMapIsConsistent();
}


export function getFlowSteps(flowId: FlowId): ReadonlyArray<FlowStep> {
  return FLOW_CONFIG[flowId].steps ?? [];
}

export function getStepForFrame(flowId: FlowId, frame: number): {
  stepIndex: number; // 1-based
  totalSteps: number;
  step: FlowStep;
} | null {
  const steps = getFlowSteps(flowId);
  for (let i = 0; i < steps.length; i++) {
    if (steps[i].frames.includes(frame)) {
      return { stepIndex: i + 1, totalSteps: steps.length, step: steps[i] };
    }
  }
  return null;
}
